import React, { useState, useEffect, Fragment, useRef } from 'react';
import { UserAccess, UserHasAccess } from 'components/user-access';
import { PageRestricted } from 'pages/errors/page-restricted';
import { Button } from "components/buttons";
import { FormTextInput } from 'components/form-fields';
import { FleetService } from 'services/FleetService';
import { Intent } from '@blueprintjs/core';
import { NotificationToaster, AlertConfirm } from 'components/notifications';
import { ListingTable } from 'components/listing';
import { useDebounce } from 'hooks/useDebounce';
import { Tooltip } from 'components/tooltip';
import { CostModal } from 'pages/fleet/fleet-settings/cost-supplier/CostModal';
import { ShowHide } from 'components/layout';

export function Cost(props) {
    const requiredAction = "FMS:Manage";
    const canAccessPage = UserHasAccess(requiredAction);

    const defaultCost = {
        id: 0,
        name: "",
        categoryId: 1
    };

    const buttonWidth = "40px";
    
    const [costSearchTerm, setCostSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [costTableData, setCostTableData] = useState([]);
    const [costIdToDelete, setCostIdToDelete] = useState(null);
    const costTableHeaders = ["Name", "Actions"];
    const tableColumnWidths = ["100%", buttonWidth, buttonWidth];
    const debounceTimeout = 500;
    const debouncedSearchTerm = useDebounce(costSearchTerm, debounceTimeout);
    const inputRef = useRef(null);

    const [isModalUpdate, setIsModalUpdate] = useState(false);
    const [showCostModal, setShowCostModal] = useState(false);
    const [costToUpdate, setCostToUpdate] = useState(defaultCost);

    function addCost() {
        if (!canAccessPage) {
            return;
        }

        setShowCostModal(true);
    }

    function onCloseCostModal() {
        setShowCostModal(false);
        resetCostToUpdate();
    }

    function onSaveCost() {
        if (!canAccessPage) {
            return;
        }

        setShowCostModal(false);
        resetCostToUpdate();
        fetchCosts();
    }

    function resetCostToUpdate() {
        setIsModalUpdate(false);
        setCostToUpdate(defaultCost);
    }

    function onEditCost(cost, e) {
        e.stopPropagation();
        setIsModalUpdate(true);
        setCostToUpdate(cost);
        setShowCostModal(true);
    }

    function handleDelete(id) {
        setCostIdToDelete(id);
    }

    function processDelete(id) {
        setLoading(true);
        FleetService.deleteCost(id).then(function () {
            NotificationToaster.show(Intent.SUCCESS, "Cost deleted successfully");
            fetchCosts();
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Could not delete the cost type");
        }).finally(function () {
            setLoading(false);
            setCostIdToDelete(null);
        });
    }

    function onSearchChange(item) {
        setCostSearchTerm(item.target.value);
    }

    function mapLocationScores(costsResponse) {
        setCostTableData(costsResponse.data.map(function (c) {
            return [
                c.name,
                <div key={`${c.id}-edit-cost-actions`} className="button-row-small inline-items">
                    <Tooltip content={`Edit cost`}>
                        <Button
                            onClick={(e) => onEditCost(c, e)}
                            icon="edit"
                            iconOnly={true}
                            minimal={true}
                            large={false}
                        />
                    </Tooltip>
                    <Tooltip content="Delete" key={`delete-cost-tooltip-${c.id}`}>
                        <Button
                            icon="trash"
                            minimal={true}
                            large={false}
                            onClick={() => handleDelete(c.id)}
                            className={"bp6-intent-danger"}
                            key={`delete-cost-${c.id}`}
                        />
                    </Tooltip>
                </div>
            ];
        }));
    }

    function fetchCosts() {
        setLoading(true);

        const costRequest = {
            requestCount: 1000,
            pageNumber: 1,
            searchTerm: costSearchTerm,
            filters: [{ key: "CategoryName", value: "asset" }]
        };

        FleetService.getCosts(costRequest).then(function (costResponse) {
            mapLocationScores(costResponse);

            setTimeout(() => {
                if (inputRef.current !== null && inputRef.current.value !== null && inputRef.current.value !== "") {
                    inputRef.current.focus();
                }
            });
        }, function () {
            NotificationToaster.show(Intent.DANGER, "Could not fetch the cost types");
        }).finally(function () {
            setLoading(false);
        });
    }

    useEffect(() => {
        setCostTableData([]);
        setCostIdToDelete(null);
    }, []);

    useEffect(() => {
        fetchCosts();
    }, [debouncedSearchTerm]);

    return (
        <UserAccess perform={requiredAction}
            yes={() => (
                <Fragment>
                    <div className="spacer-bottom">
                        <Button intent="primary" text="Add new cost" id="new-cost" onClick={addCost} loading={loading} />
                    </div>

                    <FormTextInput 
                        inputRef={inputRef}
                        placeholder="Cost Search" 
                        disabled={loading}
                        onChange={onSearchChange} 
                        value={costSearchTerm} 
                        large 
                        icon="search" 
                        id="cost-search-field" 
                        fill
                    />

                    <ListingTable
                        id="listing-table-costs"
                        headers={costTableHeaders}
                        data={costTableData}
                        columnWidths={tableColumnWidths}
                        pageable={false}
                        loadingData={loading}
                        noDataMessage={"No cost data to display"}
                        sortable={false}
                        totalRecordCount={costTableData.length}
                        shrinkLastColumn={true}
                    />

                    <ShowHide
                        evaluator={showCostModal}
                        show={(
                            <CostModal
                                showModal={showCostModal}
                                modalTitle={isModalUpdate ? "Update Cost" : "Add New Cost"}
                                isUpdate={isModalUpdate}
                                loading={false}
                                onSave={onSaveCost}
                                onCloseModal={onCloseCostModal}
                                costDetails={costToUpdate}
                            />
                        )}
                    />

                    <AlertConfirm
                        isOpen={costIdToDelete != null}
                        onConfirm={() => processDelete(costIdToDelete)}
                        title="Delete Cost"
                        onCancel={() => setCostIdToDelete(null)}>
                        <p>Are you sure you wish to delete this Cost Type?</p>
                    </AlertConfirm>
                </Fragment>
            )}
            no={() => (
                <div className="row">
                    <PageRestricted />
                </div>
            )}
        />
    );
}